/**
 * Grouping and ordering for the site Alarms tab.
 *
 * Everything here derives from fields carried on the ActiveAlarm rows
 * themselves (block_id / device_id / device_type). This is deliberate: the
 * previous site page grouped from the config.site_devices roster, so a failing
 * roster call silently flattened the whole page (#624). Grouping must survive
 * that endpoint being down.
 *
 * Ordering is STABLE, not severity-first — cards keep their position as
 * severities change. This diverges from the portfolio page (CustomerAlarms),
 * which sorts severity-desc; see the design spec §4 for the rationale.
 */

import type { ActiveAlarm, Severity } from '@/types/alarm.types';

/** Lower rank sorts first. Values are canonical DB device_type names. */
const DEVICE_TYPE_RANK: Record<string, number> = {
  battery_storage: 0,
  bms: 1,
  pcs: 2,
  generator: 3,
  meter: 4,
  pv: 5,
  solar_pv: 5,
  ev: 6,
  ev_charger: 6,
};

const UNKNOWN_DEVICE_TYPE_RANK = 7;

/** Blocks with no block_id always sort last, whatever they contain. */
const NO_BLOCK_RANK = 99;

export const NO_BLOCK_LABEL = 'Other devices';

const NO_BLOCK_KEY = '_no-block';

export function deviceTypeRank(deviceType: string): number {
  return DEVICE_TYPE_RANK[deviceType.toLowerCase()] ?? UNKNOWN_DEVICE_TYPE_RANK;
}

const SEVERITY_RANK: Record<Severity, number> = { critical: 3, warning: 2, info: 1 };

export interface DeviceGroup {
  deviceId: string;
  deviceType: string;
  rows: ActiveAlarm[];
}

export interface BlockGroup {
  /** React key — block_id, or the sentinel for the no-block bucket. */
  key: string;
  blockId: string | null;
  label: string;
  devices: DeviceGroup[];
  totalRows: number;
  counts: { critical: number; warning: number; info: number };
  highestSeverity: Severity | null;
}

export function groupAlarmsByBlockAndDevice(alarms: ActiveAlarm[]): BlockGroup[] {
  // block key -> device id -> rows
  const blocks = new Map<string, Map<string, ActiveAlarm[]>>();
  const blockIdByKey = new Map<string, string | null>();

  for (const a of alarms) {
    const key = a.block_id ?? NO_BLOCK_KEY;
    if (!blocks.has(key)) {
      blocks.set(key, new Map());
      blockIdByKey.set(key, a.block_id);
    }
    const byDevice = blocks.get(key)!;
    if (!byDevice.has(a.device_id)) byDevice.set(a.device_id, []);
    byDevice.get(a.device_id)!.push(a);
  }

  const groups: BlockGroup[] = [];

  for (const [key, byDevice] of blocks) {
    const blockId = blockIdByKey.get(key) ?? null;

    const devices: DeviceGroup[] = Array.from(byDevice, ([deviceId, rows]) => ({
      deviceId,
      // A device's rows all carry the same device_type; take the first.
      deviceType: rows[0].device_type,
      rows: [...rows].sort(compareRows),
    })).sort(
      (a, b) =>
        deviceTypeRank(a.deviceType) - deviceTypeRank(b.deviceType) ||
        a.deviceId.localeCompare(b.deviceId),
    );

    const counts = { critical: 0, warning: 0, info: 0 };
    let totalRows = 0;
    for (const d of devices) {
      for (const r of d.rows) {
        counts[r.severity] += 1;
        totalRows += 1;
      }
    }

    groups.push({
      key,
      blockId,
      label: blockId ?? NO_BLOCK_LABEL,
      devices,
      totalRows,
      counts,
      highestSeverity:
        counts.critical > 0
          ? 'critical'
          : counts.warning > 0
            ? 'warning'
            : counts.info > 0
              ? 'info'
              : null,
    });
  }

  return groups.sort((a, b) => blockRank(a) - blockRank(b) || a.label.localeCompare(b.label));
}

/** A block ranks by the highest-ranking (lowest index) device type it holds. */
function blockRank(g: BlockGroup): number {
  if (g.blockId === null) return NO_BLOCK_RANK;
  return g.devices.reduce(
    (best, d) => Math.min(best, deviceTypeRank(d.deviceType)),
    UNKNOWN_DEVICE_TYPE_RANK,
  );
}

/** Severity desc, then newest first. */
function compareRows(a: ActiveAlarm, b: ActiveAlarm): number {
  const sev = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
  if (sev !== 0) return sev;
  return new Date(b.raised_at).getTime() - new Date(a.raised_at).getTime();
}
